import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '@/lib/api/client';

interface OnboardingWizardProps {
  onComplete: () => void;
}

const STEPS = [
  { id: 'company', title: 'Company Info', icon: 'business', desc: 'Set your company name and contact details' },
  { id: 'client', title: 'Add a Client', icon: 'groups', desc: 'Add your first client to get started' },
  { id: 'job', title: 'Create a Job', icon: 'construction', desc: 'Schedule your first project' },
  { id: 'team', title: 'Invite Team', icon: 'badge', desc: 'Invite an employee or subcontractor' },
  { id: 'billing', title: 'Billing Setup', icon: 'payments', desc: 'Configure invoicing defaults' },
] as const;

const ProgressBar = ({ current }: { current: number }) => (
  <div className="flex items-center gap-2 mb-8">
    {STEPS.map((step, i) => (
      <div key={step.id} className="flex items-center gap-2">
        <div className={`flex items-center justify-center size-8 rounded-full text-sm font-bold transition-colors ${
          i < current ? 'bg-primary text-white' :
          i === current ? 'bg-primary/20 text-primary border-2 border-primary' :
          'bg-gray-100 text-gray-400'
        }`}>
          {i < current ? (
            <span className="material-symbols-outlined text-sm">check</span>
          ) : (
            <span>{i + 1}</span>
          )}
        </div>
        {i < STEPS.length - 1 && (
          <div className={`h-0.5 w-8 sm:w-12 transition-colors ${i < current ? 'bg-primary' : 'bg-gray-200'}`} />
        )}
      </div>
    ))}
  </div>
);

// Step 1 — Company Info
const CompanyStep = ({ onNext }: { onNext: () => void }) => {
  const [name, setName] = useState('OchrePoint Contracting LTD');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('Duncan, BC');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    try {
      setSaving(true);
      await apiClient.put('/settings', {
        category: 'company',
        settings: { companyName: name, companyPhone: phone, companyEmail: email, companyAddress: address },
      });
      onNext();
    } catch (e) { console.error(e); onNext(); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">Company Name *</label>
          <input value={name} onChange={e => setName(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-primary focus:border-primary outline-none"
            placeholder="Your Company Name" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
          <input value={phone} onChange={e => setPhone(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-primary focus:border-primary outline-none"
            placeholder="(250) 555-0100" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-primary focus:border-primary outline-none"
            placeholder="info@yourcompany.ca" />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
          <input value={address} onChange={e => setAddress(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-primary focus:border-primary outline-none"
            placeholder="City, Province" />
        </div>
      </div>
      <div className="flex justify-end">
        <button onClick={handleSave} disabled={saving}
          className="flex items-center gap-2 px-6 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-lg font-medium disabled:opacity-60">
          {saving ? 'Saving…' : 'Save & Continue'}
          <span className="material-symbols-outlined text-sm">arrow_forward</span>
        </button>
      </div>
    </div>
  );
};

// Step 2 — Add a Client
const ClientStep = ({ onNext, onSkip }: { onNext: () => void; onSkip: () => void }) => {
  const navigate = useNavigate();
  return (
    <div className="space-y-4">
      <p className="text-gray-600 text-sm">Clients are the homeowners or businesses you work for. Add your first client to attach to jobs and invoices.</p>
      <div className="flex gap-3">
        <button
          onClick={() => { navigate('/clients'); onNext(); }}
          className="flex items-center gap-2 px-6 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-lg font-medium"
        >
          <span className="material-symbols-outlined text-sm">add</span>
          Go to Clients
        </button>
        <button onClick={onSkip} className="px-4 py-2.5 text-gray-500 hover:text-gray-700 text-sm">
          Skip for now
        </button>
      </div>
    </div>
  );
};

// Step 3 — Create a Job
const JobStep = ({ onNext, onSkip }: { onNext: () => void; onSkip: () => void }) => {
  const navigate = useNavigate();
  return (
    <div className="space-y-4">
      <p className="text-gray-600 text-sm">Jobs are your projects — they track all work, documents, invoices, and schedules in one place.</p>
      <div className="flex gap-3">
        <button
          onClick={() => { navigate('/jobs/new'); onNext(); }}
          className="flex items-center gap-2 px-6 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-lg font-medium"
        >
          <span className="material-symbols-outlined text-sm">add</span>
          Create First Job
        </button>
        <button onClick={onSkip} className="px-4 py-2.5 text-gray-500 hover:text-gray-700 text-sm">
          Skip for now
        </button>
      </div>
    </div>
  );
};

// Step 4 — Invite Team
const TeamStep = ({ onNext, onSkip }: { onNext: () => void; onSkip: () => void }) => {
  const navigate = useNavigate();
  return (
    <div className="space-y-4">
      <p className="text-gray-600 text-sm">Invite employees to the field portal so they can view their schedule, log hours, and access documents from their phone.</p>
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
        <p className="font-medium mb-1">How invites work:</p>
        <ol className="list-decimal list-inside space-y-0.5 text-xs">
          <li>Go to Settings → Invites and generate an invite link</li>
          <li>Send the link to your employee via text or email</li>
          <li>They create an account and land on the employee portal</li>
        </ol>
      </div>
      <div className="flex gap-3">
        <button
          onClick={() => { navigate('/settings?tab=invites'); onNext(); }}
          className="flex items-center gap-2 px-6 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-lg font-medium"
        >
          <span className="material-symbols-outlined text-sm">mail</span>
          Go to Invites
        </button>
        <button onClick={onSkip} className="px-4 py-2.5 text-gray-500 hover:text-gray-700 text-sm">
          Skip for now
        </button>
      </div>
    </div>
  );
};

// Step 5 — Billing Setup
const BillingStep = ({ onFinish }: { onFinish: () => void }) => {
  const [paymentTerms, setPaymentTerms] = useState(30);
  const [defaultGst, setDefaultGst] = useState(true);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    try {
      setSaving(true);
      await apiClient.put('/settings', {
        category: 'invoicing',
        settings: { defaultPaymentTerms: paymentTerms, bcGstDefault: defaultGst },
      });
      onFinish();
    } catch (e) { console.error(e); onFinish(); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Default Payment Terms (days)</label>
        <select value={paymentTerms} onChange={e => setPaymentTerms(Number(e.target.value))}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-primary focus:border-primary outline-none">
          <option value={14}>14 days (2 weeks)</option>
          <option value={30}>30 days (net 30)</option>
          <option value={45}>45 days</option>
          <option value={60}>60 days (net 60)</option>
        </select>
      </div>
      <label className="flex items-start gap-3 cursor-pointer">
        <input type="checkbox" checked={defaultGst} onChange={e => setDefaultGst(e.target.checked)}
          className="mt-0.5 w-4 h-4 rounded border-gray-300 text-primary" />
        <div>
          <span className="text-sm font-medium text-gray-900">Enable BC GST/PST by default on new invoices</span>
          <p className="text-xs text-gray-500 mt-0.5">Pre-fills 5% GST and 7% PST on each new invoice (you can change per invoice)</p>
        </div>
      </label>
      <div className="flex justify-end">
        <button onClick={handleSave} disabled={saving}
          className="flex items-center gap-2 px-6 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-lg font-medium disabled:opacity-60">
          {saving ? 'Saving…' : 'Finish Setup'}
          <span className="material-symbols-outlined text-sm">check_circle</span>
        </button>
      </div>
    </div>
  );
};

export const OnboardingWizard = ({ onComplete }: OnboardingWizardProps) => {
  const [currentStep, setCurrentStep] = useState(0);

  const next = () => {
    if (currentStep < STEPS.length - 1) setCurrentStep(s => s + 1);
  };

  const handleFinish = async () => {
    try {
      await apiClient.put('/settings', {
        category: 'general',
        settings: { onboarding_completed: true },
      });
    } catch (e) { console.error(e); }
    onComplete();
  };

  const step = STEPS[currentStep];

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-xl">
        {/* Header */}
        <div className="px-8 pt-8 pb-4">
          <div className="flex items-center gap-3 mb-2">
            <span className="material-symbols-outlined text-primary text-3xl">construction</span>
            <div>
              <h2 className="text-xl font-black text-gray-900">Welcome to OchrePoint</h2>
              <p className="text-sm text-gray-500">Let's get your account set up — takes about 3 minutes</p>
            </div>
          </div>
          <ProgressBar current={currentStep} />
          <div className="flex items-center gap-3 mb-1">
            <div className="flex items-center justify-center size-10 rounded-full bg-primary/10">
              <span className="material-symbols-outlined text-primary">{step.icon}</span>
            </div>
            <div>
              <h3 className="font-bold text-gray-900">{step.title}</h3>
              <p className="text-xs text-gray-500">{step.desc}</p>
            </div>
          </div>
        </div>

        {/* Step content */}
        <div className="px-8 pb-8">
          {currentStep === 0 && <CompanyStep onNext={next} />}
          {currentStep === 1 && <ClientStep onNext={next} onSkip={next} />}
          {currentStep === 2 && <JobStep onNext={next} onSkip={next} />}
          {currentStep === 3 && <TeamStep onNext={next} onSkip={next} />}
          {currentStep === 4 && <BillingStep onFinish={handleFinish} />}
        </div>

        {/* Footer skip */}
        <div className="px-8 pb-4 border-t border-gray-100 pt-3 flex justify-between items-center">
          <p className="text-xs text-gray-400">Step {currentStep + 1} of {STEPS.length}</p>
          <button
            onClick={handleFinish}
            className="text-xs text-gray-400 hover:text-gray-600"
          >
            Skip all and go to dashboard
          </button>
        </div>
      </div>
    </div>
  );
};
